import memoize from 'memoize-one';

import {tryFocusMainWindow} from '../../../services/browser';
import {AppState} from '../../appState';
import {ThunkAction} from '../../appThunk';
import {buildRouter} from '../panelRoutes';
import {makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {NewPanelId} from '../panelStateBase';
import {navigateAppAsyncAction, unregisterNavigationBlockerAction} from './appPanelActions';
import {NavigationBlockerId} from './appPanelHelpers';
import {buildAppPanelRoutes} from './appPanelRoutes';
import {makeGetBlockedNavigation} from './appPanelSelectors';
import {AppContentTypesEnum, AppParameters} from './parameters/appParameters';

/*
 * Parameters-based navigation.
 */

export function pushDefaultContentTypeAction(panelId: NewPanelId): ThunkAction {
  return pushParametersAction(panelId, undefined);
}

export function pushContentTypeAction(panelId: NewPanelId, contentType: AppContentTypesEnum) {
  return pushParametersAction(panelId, {contentType});
}

export function pushParametersAndFocusAction(panelId: NewPanelId, appParameters: AppParameters | undefined) {
  // TODO: Encapsule the side-effect in a thunk.
  tryFocusMainWindow();
  return pushParametersAction(panelId, appParameters);
}

export function pushParametersAction(panelId: NewPanelId, appParameters: AppParameters | undefined) {
  return parametersAction(panelId, appParameters, {navigationShouldReplace: false});
}

export function replaceParametersAction(panelId: NewPanelId, appParameters: AppParameters | undefined) {
  return parametersAction(panelId, appParameters, {navigationShouldReplace: true});
}

type ParametersOptions = {
  navigationShouldReplace?: boolean;
  ignoreNavigationBlockerId?: NavigationBlockerId;
};

function parametersAction(
  panelId: NewPanelId,
  appParameters: AppParameters | undefined,
  {navigationShouldReplace = false, ignoreNavigationBlockerId}: ParametersOptions = {},
): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(
      navigateAppAsyncAction(appPanelId, appParameters, {navigationShouldReplace, ignoreNavigationBlockerId}),
    );
  };
}

/*
 * Path-based navigation.
 */

export function pushPathAndFocusAction(
  panelId: NewPanelId,
  path: string,
  ignoreNavigationBlockerId?: NavigationBlockerId,
) {
  // TODO: Encapsule the side-effect in a thunk.
  tryFocusMainWindow();
  return pushPathAction(panelId, path, ignoreNavigationBlockerId);
}

export function pushPathAction(
  panelId: NewPanelId,
  path: string,
  ignoreNavigationBlockerId?: NavigationBlockerId,
): ThunkAction {
  return pathAction(panelId, path, {navigationShouldReplace: false, ignoreNavigationBlockerId});
}

export function replacePathAction(panelId: NewPanelId, path: string): ThunkAction {
  return pathAction(panelId, path, {navigationShouldReplace: true});
}

type PathOptions = {
  navigationShouldReplace?: boolean;
  ignoreNavigationBlockerId?: NavigationBlockerId;
};

function pathAction(panelId: NewPanelId, path: string, options: PathOptions = {}): ThunkAction {
  const {navigationShouldReplace = false, ignoreNavigationBlockerId} = options;

  return (dispatch, getState) => {
    const parameters = tryParsePath(getState(), path);
    if (parameters === false) {
      return;
    }

    dispatch(parametersAction(panelId, parameters, {navigationShouldReplace, ignoreNavigationBlockerId}));
  };
}

export function tryParsePath(state: AppState, path: string): AppParameters | undefined | false {
  const {pathname, search} = new URL(path, 'https://example.org');
  return buildAppRouter()(state, pathname, search);
}

const buildAppRouter = memoize(() => {
  const routes = buildAppPanelRoutes();
  return buildRouter(routes);
});

/*
 * Navigation after blocking.
 */

export function unblockNavigationAction(panelId: NewPanelId, blockerId: NavigationBlockerId): ThunkAction {
  return (dispatch, getState) => {
    const blockedNavigation = makeGetBlockedNavigation()(getState(), {panelId});
    dispatch(unregisterNavigationBlockerAction(panelId, blockerId));

    if (!blockedNavigation) {
      return;
    }

    dispatch(pushParametersAction(panelId, blockedNavigation.parameters));
  };
}
